import React from "react";
import { Modal, Tag, Button, Table, Typography } from "antd";
import { ExclamationCircleFilled, CloseCircleOutlined, CopyOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

const { Title, Text, Paragraph } = Typography;

const CsvValidationModal = ({ open, onClose, errorData }) => {
  if (!errorData) return null;

  const errors = errorData.errors || [];
  const errorCount = errorData.errorCount || errors.length || 0;
  const totalRows = errorData.totalRows || 0;

  const getErrorTag = (errorMsg = "") => {
    const lower = errorMsg.toLowerCase();
    if (lower.includes("duplicate") && lower.includes("csv")) {
      return <Tag color="volcano">🔁 Duplicate in CSV</Tag>;
    }
    if (lower.includes("already exists in database") || lower.includes("db")) {
      return <Tag color="red">💾 Exists in DB</Tag>;
    }
    if (lower.includes("not found in registered") || lower.includes("vc user")) {
      return <Tag color="orange">👤 User Not Found</Tag>;
    }
    if (lower.includes("amount") || lower.includes("number")) {
      return <Tag color="gold">🔢 Invalid Amount</Tag>;
    }
    if (lower.includes("missing mandatory") || lower.includes("field")) {
      return <Tag color="magenta">⚠️ Missing Field</Tag>;
    }
    return <Tag color="default">🛑 Error</Tag>;
  };

  const columns = [
    {
      title: "Excel Row",
      dataIndex: "row",
      key: "row",
      width: 100,
      render: (row) => (
        <span style={{ fontWeight: "700", color: "#1890ff", fontSize: "14px" }}>
          Row {row}
        </span>
      ),
    },
    {
      title: "Invoice No",
      dataIndex: "invoice_no",
      key: "invoice_no",
      width: 150,
      render: (inv) =>
        inv ? (
          <code
            style={{
              backgroundColor: "#f5f5f5",
              padding: "3px 8px",
              borderRadius: "4px",
              fontWeight: "600",
              color: "#333",
              border: "1px solid #e8e8e8",
            }}
          >
            {inv}
          </code>
        ) : (
          <Text type="secondary" italic>
            (Missing)
          </Text>
        ),
    },
    {
      title: "Category",
      key: "category",
      width: 160,
      render: (_, record) => getErrorTag(record.error),
    },
    {
      title: "Error Details & Solution",
      dataIndex: "error",
      key: "error",
      render: (text) => (
        <Text style={{ color: "#cf1322", fontWeight: "500", fontSize: "13px" }}>
          {text}
        </Text>
      ),
    },
  ];

  const handleCopyErrors = () => {
    const textToCopy = errors
      .map(
        (err) =>
          `Row ${err.row}${err.invoice_no ? ` [${err.invoice_no}]` : ""}: ${err.error}`
      )
      .join("\n");

    navigator.clipboard.writeText(textToCopy);
    toast.info("Error summary copied to clipboard!", { autoClose: 2000 });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={850}
      centered
      footer={[
        <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyErrors}>
          Copy Error List
        </Button>,
        <Button key="close" type="primary" danger onClick={onClose}>
          Close & Fix CSV
        </Button>,
      ]}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <ExclamationCircleFilled style={{ color: "#ff4d4f", fontSize: "24px" }} />
          <div>
            <Title level={4} style={{ margin: 0, color: "#1f1f1f" }}>
              CSV Pre-Validation Failed
            </Title>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              Found <strong>{errorCount} issue(s)</strong> {totalRows > 0 ? `across ${totalRows} row(s)` : ""}
            </Text>
          </div>
        </div>
      }
    >
      <div
        style={{
          background: "#fff1f0",
          border: "1px solid #ffccc7",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
          marginTop: "12px",
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "18px", marginTop: "3px" }} />
        <div>
          <Text strong style={{ color: "#cf1322", fontSize: "14px" }}>
            Zero (0) records were saved to the database.
          </Text>
          <Paragraph style={{ margin: "4px 0 0 0", color: "#595959", fontSize: "13px" }}>
            To prevent corrupted or duplicate records, all rows were tested in memory before insertion.
            Please review and correct the errors below in your CSV file and upload again.
          </Paragraph>
        </div>
      </div>

      <Table
        dataSource={errors.map((err, idx) => ({ ...err, key: idx }))}
        columns={columns}
        pagination={errors.length > 5 ? { pageSize: 5 } : false}
        size="middle"
        bordered
        style={{ marginTop: "10px" }}
      />
    </Modal>
  );
};

export default CsvValidationModal;
